import { ORDER_BEHAVIOR, ORDER_TYPE, type IEquity } from '~/api/interface/rights';
import { RIGHTS_TIME } from '~/constants';
import BaseState, { type ITitleClass } from './base';
import CornerMark from './corner-mark.vue';

/** 个人版 */
export default class Rights10 extends BaseState {
  TitleView = defineComponent({
    name: 'TitleView3',
    props: {
      titleClass: {
        type: Object as PropType<ITitleClass>,
        required: true,
      },
      rights: {
        type: Object as PropType<IEquity>,
        required: true,
      },
      hovered: {
        type: Boolean,
        default() {
          return false;
        },
      },
    },
    setup(props) {
      const { rights } = props;
      const { $t } = useNuxtApp();
      return () => (
        <>
          <div>
            <div class="title-name">{rights.rights_name}</div>
          </div>
          <div class="sub-title">{rights.subject}</div>
          {isChinese() ? (
            <CornerMark text={$t('ge-ren-shang-yong-shou-quan-bu-ke-qi-ye-shang-yong')} />
          ) : null}
        </>
      );
    },
  });

  PriceView = defineComponent({
    props: {
      rights: {
        type: Object as PropType<IEquity>,
        required: true,
      },
      titleClass: {
        type: Object as PropType<ITitleClass>,
        required: true,
      },
    },
    setup(props) {
      const { rights } = props;
      const state = useRightsConfig();
      const MONTH = 1;
      const YEAR = 12;
      const { $t } = useNuxtApp();

      return () => {
        const month = rights.rights_by_time.find(
          t =>
            (t.time_identity === RIGHTS_TIME().month || t.months === MONTH) &&
            (isChinese() ? !t.buy_type : t.buy_type),
        );

        const year = rights.rights_by_time.find(
          t =>
            (t.time_identity === RIGHTS_TIME().year || t.months >= YEAR) &&
            (isChinese() ? !t.buy_type : t.buy_type),
        );
        if (!month || !year) {
          return null;
        }

        const diffPrice = formatPrice(month.price * 12 - year.price)?.value;
        const showMonthly = !isGlobalVersion() || state.value.cycle === 'monthly';
        const showAnnual = !isGlobalVersion() || state.value.cycle === 'annual';

        const displayLowestPrice = Math.min(year.price / 12, month.price);

        return (
          <div class={!isChinese() ? 'pt-[43px] pb-[16px]' : 'pt-[38px] pb-[34px]'}>
            <div class=" flex justify-around text-center text-font-1">
              {isChinese() && (
                <div class="flex-y-center items-baseline text-[20px] text-left w-full discount-price">
                  <div class="price-suffix">低至</div>
                  <div class="price-prefix ml-[5px]">{formatPrice(month.price).prefix}</div>
                  <div class="price-num">{formatPrice(displayLowestPrice).value.toFixed(1)}</div>
                  <div class="price-suffix">{`/${$t('yue')}`} 起</div>
                </div>
              )}
              {showMonthly && !isChinese() ? (
                <div class="flex-1 flex justify-between items-baseline text-[#2F7791]">
                  <div class="flex items-baseline">
                    <div class="price-prefix">{formatPrice(month.price).prefix}</div>
                    <div class="price-num">{formatPrice(month.price).value}</div>
                    <div class="price-suffix">{`/${$t('yue')}`}</div>
                  </div>
                  <div class="text-[14px] leading-[28px] line-through opacity-80">
                    {formatPrice(month.show_price).string} /{$t('yue')}
                  </div>
                </div>
              ) : null}
              {showAnnual && !isChinese() ? (
                <div class="flex-1 flex justify-between items-baseline text-[#2F7791]">
                  {!isGlobalVersion() && Number(diffPrice) > 0 && (
                    <div class="rights-extra">{$t('li-sheng-diffprice-yuan', [diffPrice])}</div>
                  )}
                  <div class="flex items-baseline">
                    <div class="price-prefix">{formatPrice(year.price).prefix}</div>
                    <div class="price-num">{formatPrice(year.price / 12).value}</div>

                    <div class="price-suffix">/{$t('yue')}</div>
                  </div>
                  <div class="text-[12px] leading-[28px] line-through">
                    {formatPrice(year.show_price / 12).string}/{$t('yue')}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        );
      };
    },
  });

  get ButtonView() {
    const { $t } = useNuxtApp();

    if (this.isSameRights()) {
      return this.getButton({
        text: $t('li-ji-xu-fei'),
        cb: this.renewOrder.bind(this),
      });
    }
    return this.getButton({
      text: $t('li-ji-ding-yue'),
      cb: this.newOrder.bind(this),
    });
  }

  get StickyButtonView() {
    const { $t } = useNuxtApp();

    if (this.isSameRights()) {
      return this.getButton({
        text: $t('li-ji-xu-fei'),
        cb: this.renewOrder.bind(this),
      });
    }
    return this.getButton({
      text: $t('li-ji-ding-yue'),
      cb: this.newOrder.bind(this),
    });
  }

  buyLimit() {
    const { $t } = useNuxtApp();

    if (!this.isUserLogin) {
      this.toCreateRights16();
      return true;
    }
    if (this.isPersonalAccount) {
      if (this.hasManagedRights1516()) {
        message.info(
          <div>
            {$t('dang-qian-wei-ge-ren-zhang-hao-ru-xu-gou-mai-tuan-dui-tao-can-qing')}
            <a class="mx-[4px]" onClick={this.switchAccount}>
              {$t('qie-huan-zhang-hao')}
            </a>
            {$t('jin-hang-gou-mai')}
            {$t('huo')}
            <a class="mx-[4px]" onClick={this.toCreateRights16}>
              {$t('xin-jian-tuan-dui-sheng-ji')}
            </a>
          </div>,
        );
      } else {
        this.toCreateRights16();
      }

      return true;
    }
    if (this.isRights2122) {
      if (this.hasManagedRights1516()) {
        message.info(
          <div>
            {$t(
              'dang-qian-yi-gou-mai-qi-ye-tao-can-zan-bu-zhi-chi-gou-mai-ru-men-ban-tao-can-shi-fou',
            )}
            <a onClick={this.switchAccount}>{$t('qie-huan-zhang-hao')}</a>
            {$t('gou-mai-huo')}
            <a onClick={this.toCreateRights16}>{$t('xin-jian-tuan-dui-sheng-ji')}</a>，
            {$t('ru-you-qi-ta-wen-ti-qing-lian-xi-nin-de-shang-wu-fu-ze-ren-0')}
          </div>,
        );
      } else {
        message.info(
          <div>
            {$t(
              'dang-qian-yi-gou-mai-qi-ye-tao-can-zan-bu-zhi-chi-gou-mai-ru-men-ban-tao-can-shi-fou',
            )}
            <a onClick={this.toCreateRights16}>{$t('xin-jian-tuan-dui-sheng-ji')}</a>，
            {$t('ru-you-qi-ta-wen-ti-qing-lian-xi-nin-de-shang-wu-fu-ze-ren-0')}
          </div>,
        );
      }
      return true;
    }
    if (this.isOrgMember) {
      if (this.hasManagedRights1516()) {
        message.info(
          <div>
            {$t(
              'tuan-dui-tao-can-jin-yun-xu-guan-li-yuan-jin-hang-gou-mai-sheng-ji-qing-lian-xi-guan-li-yuan-jin-hang-gou-mai-ru-xu-sheng-ji-qi-ta-zhang-hao-qing',
            )}
            <a onClick={this.switchAccount}>{$t('qie-huan-zhang-hao')}</a>
            {$t('huo')}
            <a onClick={this.toCreateRights16}>{$t('xin-jian-tuan-dui-sheng-ji')}</a>
          </div>,
        );
      } else {
        message.info(
          <div>
            {$t(
              'tuan-dui-tao-can-jin-yun-xu-guan-li-yuan-jin-hang-gou-mai-sheng-ji-qing-lian-xi-guan-li-yuan-jin-hang-gou-mai-huo',
            )}
            <a onClick={this.toCreateRights16}>{$t('xin-jian-tuan-dui-sheng-ji')}</a>
          </div>,
        );
      }
      return true;
    }
    return false;
  }

  public async newOrder(meta?: string) {
    const { $t } = useNuxtApp();

    if (!this.isUserLogin) {
      this.toLogin();
      return false;
    }
    if (this.isOrgAccount) {
      message.info(
        <div>
          {$t('dang-qian-wei-tuan-dui-zhang-hao-ru-xu-gou-mai-ge-ren-ban-tao-can-qing')}
          <a onClick={this.switchAccount}>{$t('qie-huan-zhang-hao')}</a>
          {$t('jin-hang-gou-mai')}
        </div>,
      );
      return;
    }
    await this.buy({
      title: this.rights.rights_name,
      behavior: ORDER_BEHAVIOR.BUY,
      order_type: ORDER_TYPE.PACKAGE,
      meta,
    });
    return true;
  }

  public async renewOrder(meta?: string) {
    await this.buy({
      title: this.rights.rights_name,
      behavior: ORDER_BEHAVIOR.RENEW,
      order_type: ORDER_TYPE.PACKAGE,
      meta,
    });
    return true;
  }
}
